import { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cardImage } from "./assets.tsx";
import type { GameEvent, State } from "./types.ts";

type Endpoint = { kind: "deck" | "discard" | "hand" | "equipment"; owner: string; card: string };
type Geometry = { x: number; y: number; width: number; height: number; a: number; b: number; c: number; d: number };
type Surface = { node: HTMLElement; hide?: HTMLElement | undefined; geometry: Geometry };
type Move = { card: string; face: string; deck: string; from: Endpoint; to: Endpoint; equipment: boolean };
type Flight = { node: HTMLElement; animation: Animation; move: Move; destination: Surface; restore: () => void };

const endpoint = (kind: Endpoint["kind"], owner: string, card = ""): Endpoint => ({ kind, owner, card });
const endpointKey = (value: Endpoint) => JSON.stringify([value.kind, value.owner, value.card]);
const text = (value: unknown): string => typeof value === "string" ? value : "";
const deckOf = (id: string): string => ["hakurei", "moriya", "kleinji"].find((deck) => id.startsWith(`${deck}_`)) ?? "";
const eventKey = (event: GameEvent) => `${event.seq}|${event.type}|${JSON.stringify(event.data ?? {})}`;
const ownership = (state: State) => new Map(state.players.flatMap((player) => player.equipment.map((card) => [card, player.id] as const)));
const relevant: Record<string, true> = { card_drawn: true, klein_drawn: true, klein_given: true, card_resolved: true, klein_resolved: true, equipment_gained: true, equipment_lost: true };

// Preserve the card's full transformed quadrilateral, not its rotated bounding-box size.
// The table uses affine CSS transforms (including parent seat rotations).
function geometry(node: HTMLElement): Geometry | null {
  const rect = node.getBoundingClientRect();
  if (!node.isConnected || !node.offsetWidth || !node.offsetHeight || rect.width <= 0 || rect.height <= 0 ||
    rect.right <= 0 || rect.bottom <= 0 || rect.left >= innerWidth || rect.top >= innerHeight) return null;
  let matrix = new DOMMatrix();
  for (let parent: HTMLElement | null = node; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (style.display === "none" || style.contentVisibility === "hidden") return null;
    if (style.transform !== "none") {
      const transform = new DOMMatrix(style.transform);
      matrix = transform.multiply(matrix);
    }
    // A completely clipped endpoint cannot receive a visible flight.
    if (parent !== node && /hidden|clip|scroll|auto/.test(`${style.overflowX} ${style.overflowY}`)) {
      const clip = parent.getBoundingClientRect();
      if (rect.right <= clip.left || rect.left >= clip.right || rect.bottom <= clip.top || rect.top >= clip.bottom) return null;
    }
  }
  // Resting card flips include perspective() in their CSS matrix, but their
  // z=0 face is still affine. Do not invent a rectangular landing mid-flip.
  if (Math.abs(matrix.m14) > 0.0000001 || Math.abs(matrix.m24) > 0.0000001) return null;
  const width = node.offsetWidth;
  const height = node.offsetHeight;
  const { a, b, c, d } = matrix;
  return {
    x: rect.left - Math.min(0, a * width, c * height, a * width + c * height),
    y: rect.top - Math.min(0, b * width, d * height, b * width + d * height),
    width, height, a, b, c, d,
  };
}

function surface(value: Endpoint): Surface | null {
  const owner = CSS.escape(value.owner);
  const card = CSS.escape(value.card);
  let node: HTMLElement | null;
  let hide: HTMLElement | undefined;
  if (value.kind === "deck") {
    node = document.querySelector(`.deck[data-deck="${owner}"] .card-back-art`);
  } else if (value.kind === "discard") {
    node = document.querySelector(`[data-discard-deck="${owner}"]`);
  } else {
    const attr = value.kind === "hand" ? "data-hand-card" : "data-equipment-card";
    const figure = document.querySelector<HTMLElement>(`[${attr}="${card}"][data-card-owner="${owner}"]`);
    node = figure?.querySelector<HTMLElement>("img, .card-back-art") ?? figure;
    if (figure) hide = figure;
    // Other players' private hand is represented by their seated card, never by
    // an invented point in the middle of the screen or the whole player panel.
    if (!node) {
      const media = document.querySelector<HTMLElement>(`[data-player-id="${owner}"] .player-card-media`);
      const frontVisible = media?.closest(".is-revealed") || (media?.closest(".player-hud .is-me") && media.querySelector(".player-card-front .card-face"));
      node = media?.querySelector<HTMLElement>(frontVisible ? ".player-card-front" : ".player-card-back") ?? media;
    }
  }
  const rect = node && geometry(node);
  return node && rect ? { node, hide, geometry: rect } : null;
}

function transform(rect: Geometry, width: number, height: number): string {
  return `matrix(${rect.a * rect.width / width},${rect.b * rect.width / width},${rect.c * rect.height / height},${rect.d * rect.height / height},${rect.x},${rect.y})`;
}

function rememberSurfaces(cache: Map<string, Geometry>): void {
  const next = new Map<string, Geometry>();
  for (const node of document.querySelectorAll<HTMLElement>("[data-hand-card][data-card-owner], [data-equipment-card][data-card-owner]")) {
    const kind = node.hasAttribute("data-hand-card") ? "hand" : "equipment";
    const value = endpoint(kind, node.dataset.cardOwner!, node.getAttribute(`data-${kind}-card`)!);
    const measured = surface(value);
    if (measured) next.set(endpointKey(value), measured.geometry);
  }
  for (const node of document.querySelectorAll<HTMLElement>("[data-player-id] .player-card-media")) {
    const player = node.closest<HTMLElement>("[data-player-id]")!.dataset.playerId!;
    const measured = surface(endpoint("hand", player));
    if (measured) next.set(`player:${player}`, measured.geometry);
  }
  for (const node of document.querySelectorAll<HTMLElement>(".deck[data-deck], [data-discard-deck]")) {
    const kind = node.hasAttribute("data-deck") ? "deck" : "discard";
    const value = endpoint(kind, kind === "deck" ? node.dataset.deck! : node.dataset.discardDeck!);
    const measured = surface(value);
    if (measured) next.set(endpointKey(value), measured.geometry);
  }
  // Preserve the last committed source after its card leaves the DOM.
  // Reinsert current entries so removed cards are evicted first.
  for (const [key, rect] of next) {
    cache.delete(key);
    cache.set(key, rect);
  }
  while (cache.size > 128) cache.delete(cache.keys().next().value!);
}

/** Moves visual cards between real endpoints of the currently presented action. */
export function CardMotionLayer({ events, state, me, sceneKey, onComplete }: {
  events: GameEvent[]; state: State; me: string; sceneKey: string | null; onComplete: (key: string) => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const schedule = useRef<() => void>(() => {});
  const retarget = useRef<() => void>(() => {});
  const runtime = useRef<{
    previous: State | null;
    seen: Set<string>;
    cache: Map<string, Geometry>;
    events: GameEvent[];
    changes: Map<string, { from: string; to: string; deck: string }>;
    active: Map<string, Flight>;
    landed: Map<string, { destination: string; time: number }>;
    frame: number;
    state: State;
    sceneKey: string | null;
    completedKey: string | null;
    onComplete: (key: string) => void;
  }>({ previous: null, seen: new Set(), cache: new Map(), events: [], changes: new Map(), active: new Map(), landed: new Map(), frame: 0, state, sceneKey: null, completedKey: null, onComplete });

  useLayoutEffect(() => {
    const current = runtime.current;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");

    function completeIfIdle() {
      queueMicrotask(() => {
        if (!current.sceneKey || current.sceneKey === current.completedKey ||
          current.frame || current.events.length || current.changes.size || current.active.size) return;
        current.completedKey = current.sceneKey;
        current.onComplete(current.sceneKey);
      });
    }

    function finish(card: string, flight: Flight) {
      flight.animation.onfinish = null;
      flight.animation.oncancel = null;
      for (const animation of flight.node.getAnimations({ subtree: true })) animation.cancel();
      flight.restore();
      flight.node.remove();
      if (current.active.get(card) === flight) current.active.delete(card);
      completeIfIdle();
    }

    function cancelAll() {
      cancelAnimationFrame(current.frame);
      current.frame = 0;
      current.events.length = 0;
      current.changes.clear();
      for (const [card, flight] of current.active) finish(card, flight);
      current.cache.clear();
      rememberSurfaces(current.cache);
      completeIfIdle();
    }

    const flush = () => {
      current.frame = 0;
      const batch = current.events.splice(0).sort((a, b) => a.seq - b.seq);
      const changes = new Map(current.changes);
      current.changes.clear();
      if (!root.current || reduced.matches || typeof Element.prototype.animate !== "function") {
        completeIfIdle();
        return;
      }
      const owners = ownership(current.state);
      const moves = new Map<string, Move>();
      const merge = (move: Move) => {
        const prior = moves.get(move.card);
        moves.set(move.card, prior ? { ...move, from: prior.from, face: move.face || prior.face } : move);
      };
      for (const event of batch) {
        const data = event.data ?? {};
        const face = text(data.card && typeof data.card === "object" ? (data.card as Record<string, unknown>).id : "");
        const id = face || text(data.card_id);
        const player = text(data.player);
        const deck = text(data.deck) || (event.type.startsWith("klein_") ? "kleinji" : deckOf(id));
        const key = id || `hidden:${event.seq}:${player}:${deck}`;
        const hand = endpoint("hand", player, id);
        const draw = event.type === "card_drawn" || event.type === "klein_drawn";
        if (draw) {
          if (!deck) continue;
          merge({ card: key, face, deck, from: endpoint("deck", deck), to: hand, equipment: false });
        } else if (event.type === "klein_given") {
          merge({ card: key, face, deck, from: hand, to: endpoint("hand", text(data.target), id), equipment: false });
        } else if (event.type === "card_resolved" || event.type === "klein_resolved") {
          if (deck) merge({ card: key, face, deck, from: hand, to: endpoint("discard", deck), equipment: false });
        } else if (event.type === "equipment_gained") {
          const change = changes.get(id);
          const sourceOwner = change?.from || text(data.by);
          // Prefer a real former owner or the pending card in hand. A deck source
          // is only justified by a matching draw or a decreased deck count.
          const cachedHand = current.cache.has(endpointKey(hand));
          const from = sourceOwner ? endpoint("equipment", sourceOwner, id) : cachedHand ? hand : change?.deck ? endpoint("deck", change.deck) : null;
          if (from || moves.has(key)) merge({ card: key, face: id, deck, from: from ?? moves.get(key)!.from, to: endpoint("equipment", player, id), equipment: true });
        } else if (event.type === "equipment_lost" && !owners.has(id) && deck) {
          merge({ card: key, face: id, deck, from: endpoint("equipment", player, id), to: endpoint("discard", deck), equipment: true });
        }
      }
      // Loot and other engine paths can move equipment without an individual
      // gained/lost event. The public ownership diff supplies those transitions.
      for (const [card, change] of changes) {
        const existing = moves.get(card);
        const deck = deckOf(card);
        const from = change.from ? endpoint("equipment", change.from, card) :
          current.cache.has(endpointKey(endpoint("hand", change.to, card))) ? endpoint("hand", change.to, card) :
          change.deck ? endpoint("deck", change.deck) : null;
        const to = change.to ? endpoint("equipment", change.to, card) : deck ? endpoint("discard", deck) : null;
        if (to && (from || existing)) moves.set(card, { card, face: card, deck, from: existing?.from ?? from!, to, equipment: true });
      }
      for (const move of moves.values()) {
        const destination = surface(move.to);
        const active = current.active.get(move.card);
        const targetKey = endpointKey(move.to);
        if (active && endpointKey(active.move.to) === targetKey) continue;
        const landed = current.landed.get(move.card);
        if (move.equipment && landed?.destination === targetKey && performance.now() - landed.time < 1500) continue;
        const from = active ? geometry(active.node) : current.cache.get(endpointKey(move.from)) ?? surface(move.from)?.geometry ??
          (move.from.kind === "hand" || move.from.kind === "equipment" ? current.cache.get(`player:${move.from.owner}`) : undefined);
        if (active) finish(move.card, active);
        if (!from || !destination) continue;
        // A removed hand/equipment card retains its last committed source pose.
        if (endpointKey(move.from) === targetKey) continue;
        const { width, height } = destination.geometry;
        const node = document.createElement("div");
        node.dataset.cardFlight = move.card;
        Object.assign(node.style, {
          position: "absolute", left: "0", top: "0", width: `${width}px`, height: `${height}px`,
          transformOrigin: "0 0", pointerEvents: "none", borderRadius: getComputedStyle(destination.node).borderRadius,
          overflow: "hidden", willChange: "transform",
        });
        const material = document.createElement("div");
        Object.assign(material.style, { position: "absolute", inset: "0", background: "#25221e", transformOrigin: "50% 50%" });
        node.append(material);
        const drawing = move.from.kind === "deck" && !active;
        const duration = drawing ? 850 : 620;
        // Clone only the public back artwork, never a private endpoint face.
        const backSource = document.querySelector<HTMLElement>(`.deck[data-deck="${CSS.escape(move.deck)}"] .card-back-art`);
        const back = backSource?.cloneNode(true) as HTMLElement | undefined;
        if (back) {
          back.className = `card-back-art deck-back-${move.deck}`;
          Object.assign(back.style, { position: "absolute", inset: "0", width: "100%", height: "100%", transform: "none", boxSizing: "border-box", visibility: "visible" });
          material.append(back);
        }
        const image = move.face && cardImage(move.face);
        if (image) {
          const img = document.createElement("img");
          img.src = image;
          img.alt = "";
          img.draggable = false;
          Object.assign(img.style, { position: "absolute", inset: "0", display: "block", width: "100%", height: "100%", objectFit: "fill" });
          img.onerror = () => img.remove();
          material.append(img);
          if (drawing && back) {
            // The card turns edge-on, changes side, then lies face-up. Only the
            // material folds; the outer node keeps its exact endpoint transform.
            material.animate([
              { transform: "scaleX(1)", offset: 0 },
              { transform: "scaleX(1)", offset: 0.18 },
              { transform: "scaleX(0)", offset: 0.38 },
              { transform: "scaleX(1)", offset: 0.58 },
              { transform: "scaleX(1)", offset: 1 },
            ], { duration, fill: "both" });
            img.animate([
              { visibility: "hidden", offset: 0 },
              { visibility: "hidden", offset: 0.379 },
              { visibility: "visible", offset: 0.38 },
              { visibility: "visible", offset: 1 },
            ], { duration, fill: "both" });
          }
        }
        if (!back && !image) continue;
        const hidden = destination.hide;
        const oldVisibility = hidden?.style.getPropertyValue("visibility") ?? "";
        const oldPriority = hidden?.style.getPropertyPriority("visibility") ?? "";
        hidden?.style.setProperty("visibility", "hidden", "important");
        destination.geometry = geometry(destination.node) ?? destination.geometry;
        root.current.append(node);
        const start = transform(from, width, height);
        const end = transform(destination.geometry, width, height);
        node.style.transform = end;
        const animation = node.animate([{ transform: start }, { transform: end }], { duration, easing: "cubic-bezier(.22,.65,.24,1)", fill: "both" });
        const flight: Flight = {
          node, animation, move, destination,
          restore: () => {
            if (!hidden) return;
            if (oldVisibility) hidden.style.setProperty("visibility", oldVisibility, oldPriority);
            else hidden.style.removeProperty("visibility");
          },
        };
        current.active.set(move.card, flight);
        animation.onfinish = () => {
          current.landed.set(move.card, { destination: targetKey, time: performance.now() });
          while (current.landed.size > 128) current.landed.delete(current.landed.keys().next().value!);
          finish(move.card, flight);
        };
        animation.oncancel = () => finish(move.card, flight);
      }
      completeIfIdle();
    };

    // Keep processing available to each committed React snapshot without an
    // effect cleanup cancelling unrelated in-progress transfers.
    schedule.current = () => {
      if (!current.frame) current.frame = requestAnimationFrame(flush);
    };
    const onViewportChange = () => {
      current.cache.clear();
      retarget.current();
      rememberSurfaces(current.cache);
    };
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    reduced.addEventListener("change", cancelAll);
    return () => {
      schedule.current = () => {};
      current.sceneKey = null;
      cancelAll();
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
      reduced.removeEventListener("change", cancelAll);
    };
  }, []);

  useLayoutEffect(() => {
    const current = runtime.current;
    current.state = state;
    current.sceneKey = sceneKey;
    current.onComplete = onComplete;
    const before = current.previous;
    const oldOwners = before ? ownership(before) : new Map<string, string>();
    const newOwners = ownership(state);
    if (before && before !== state) {
      for (const card of new Set([...oldOwners.keys(), ...newOwners.keys()])) {
        const from = oldOwners.get(card) ?? "";
        const to = newOwners.get(card) ?? "";
        if (from === to) continue;
        const prior = current.changes.get(card);
        const deck = deckOf(card);
        const drawn = deck && (before.decks[deck]?.remaining ?? 0) > (state.decks[deck]?.remaining ?? 0);
        current.changes.set(card, { from: prior?.from ?? from, to, deck: drawn ? deck : prior?.deck ?? "" });
      }
    }
    for (const event of events) {
      const key = eventKey(event);
      if (current.seen.has(key)) continue;
      current.seen.add(key);
      // Only queued live scenes reach this layer; snapshots/history have no scene key.
      if (sceneKey && relevant[event.type] && (event.visibility !== "private" || event.to?.includes(me))) current.events.push(event);
    }
    if (current.seen.size > 512) current.seen = new Set(events.map(eventKey));
    current.previous = state;
    retarget.current = () => {
    for (const flight of current.active.values()) {
      const destination = surface(flight.move.to);
      const position = geometry(flight.node);
      // Keep the current pose if a presentation transition removed the destination.
      if (!destination || (flight.destination.hide && !destination.hide)) continue;
      if (!position) {
        flight.animation.cancel();
        continue;
      }
      const previous = flight.destination;
      const next = destination.geometry;
      const width = flight.node.offsetWidth;
      const height = flight.node.offsetHeight;
      if (transform(previous.geometry, width, height) === transform(next, width, height) && previous.hide === destination.hide) continue;
      flight.restore();
      const hidden = destination.hide;
      const visibility = hidden?.style.getPropertyValue("visibility") ?? "";
      const priority = hidden?.style.getPropertyPriority("visibility") ?? "";
      hidden?.style.setProperty("visibility", "hidden", "important");
      flight.restore = () => {
        if (!hidden) return;
        if (visibility) hidden.style.setProperty("visibility", visibility, priority);
        else hidden.style.removeProperty("visibility");
      };
      flight.destination = destination;
      const end = transform(next, width, height);
      flight.node.style.transform = end;
      (flight.animation.effect as KeyframeEffect).setKeyframes([{ transform: transform(position, width, height) }, { transform: end }]);
      flight.animation.currentTime = 0;
    }
    };
    retarget.current();
    // Preserve removed sources, and measure new destinations after React commits.
    rememberSurfaces(current.cache);
    if (current.events.length || current.changes.size || (sceneKey && sceneKey !== current.completedKey)) schedule.current();
  });

  return createPortal(<div ref={root} aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 110, overflow: "visible" }} />, document.body);
}
